import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/gate-check/StatusBadge";
import {
    ArrowRight, Users, ClockCounterClockwise, DoorOpen, Warning, Car, Package,
} from "@phosphor-icons/react";
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell,
} from "recharts";

export default function Dashboard() {
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [inside, setInside] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [s, i] = await Promise.all([
                    api.get("/gate-check/dashboard/stats"),
                    api.get("/gate-check/visits/currently-inside"),
                ]);
                setStats(s.data);
                setInside(i.data);
            } catch (e) { console.error(e); }
        })();
    }, []);

    return (
        <div>
            <PageHeader
                overline="Gate Operations · Today"
                title="Daily Overview"
                subtitle="Live snapshot of visitor traffic across all gates."
                actions={
                    <Button
                        onClick={() => nav("/checkpoint/checkin")}
                        data-testid="btn-goto-checkin"
                        className="bg-brand hover:bg-brand/90 text-white h-11 px-5"
                    >
                        New Check-in <ArrowRight className="ml-2" size={16} weight="bold" />
                    </Button>
                }
            />
            <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard
                        label="Currently Inside"
                        value={stats?.currently_inside ?? "-"}
                        icon={<DoorOpen size={20} weight="duotone" />}
                        accent="text-brand"
                        testid="metric-inside"
                        to="/checkpoint/visitors?status=checked_in"
                    />
                    <MetricCard
                        label="Today's Check-ins"
                        value={stats?.total_today ?? "-"}
                        icon={<Users size={20} weight="duotone" />}
                        testid="metric-today"
                        to="/checkpoint/visitors?today=in"
                    />
                    <MetricCard
                        label="Pending Approval"
                        value={stats?.pending_approval ?? "-"}
                        icon={<ClockCounterClockwise size={20} weight="duotone" />}
                        accent="text-amber-600"
                        testid="metric-pending"
                        to="/checkpoint/visitors?status=pending"
                    />
                    <MetricCard
                        label="Checked Out Today"
                        value={stats?.checked_out_today ?? "-"}
                        icon={<Warning size={20} weight="duotone" />}
                        testid="metric-out"
                        to="/checkpoint/visitors?status=checked_out&today=out"
                    />
                    <MetricCard
                        label="Vehicles Today"
                        value={stats?.vehicles_today ?? "-"}
                        icon={<Car size={20} weight="duotone" />}
                        testid="metric-vehicles"
                        to="/checkpoint/vehicles"
                    />
                    <MetricCard
                        label="Deliveries Today"
                        value={stats?.deliveries_today ?? "-"}
                        icon={<Package size={20} weight="duotone" />}
                        testid="metric-deliveries"
                        to="/checkpoint/deliveries"
                    />
                    <MetricCard
                        label="Blacklisted"
                        value={stats?.blacklisted ?? "-"}
                        icon={<Warning size={20} weight="duotone" />}
                        accent="text-red-600"
                        testid="metric-blacklisted"
                        to="/checkpoint/blacklist"
                    />
                    <MetricCard
                        label="All-Time Visitors"
                        value={stats?.total_visitors_all_time ?? "-"}
                        icon={<Users size={20} weight="duotone" />}
                        testid="metric-total"
                        to="/checkpoint/visitors"
                    />
                </div>

                <Card className="p-5 tech-card">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="overline">Traffic · Last 24h</div>
                            <div className="font-display text-xl font-bold tracking-tight">Hourly Check-ins</div>
                        </div>
                    </div>
                    <div className="h-64" data-testid="chart-hourly">
                        <ResponsiveContainer>
                            <BarChart data={stats?.hourly || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <Tooltip
                                    contentStyle={{
                                        background: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        borderRadius: 6,
                                        fontSize: 12,
                                    }}
                                />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {(stats?.hourly || []).map((_, i) => (
                                        <Cell key={i} fill="#7A1F2E" />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="tech-card overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                        <div>
                            <div className="overline">Live · On Premises</div>
                            <div className="font-display text-xl font-bold tracking-tight">
                                Currently Inside · {inside.length}
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            className="h-9"
                            onClick={() => nav("/checkpoint/emergency")}
                            data-testid="btn-open-emergency"
                        >
                            Emergency View
                        </Button>
                    </div>
                    <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
                        {inside.length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                No visitors currently inside.
                            </div>
                        )}
                        {inside.map((v) => (
                            <div key={v.id} className="px-5 py-3 flex items-center gap-4">
                                <div className="h-10 w-10 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold">
                                    {v.visitor_name?.[0]?.toUpperCase() || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold truncate">{v.visitor_name}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {v.company || "Individual"} · Host: {v.host_name || "N/A"} · Gate: {v.gate_name}
                                    </div>
                                </div>
                                <StatusBadge status={v.status} />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => nav(`/checkpoint/visitors?id=${v.id}`)}
                                    data-testid={`btn-view-${v.id}`}
                                >
                                    View
                                </Button>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}

function MetricCard({ label, value, icon, accent, testid, to }) {
    const nav = useNavigate();
    return (
        <Card
            className="tech-card p-4 press-effect cursor-pointer transition-colors hover:border-brand/50 hover:shadow-md"
            data-testid={testid}
            onClick={to ? () => nav(to) : undefined}
            role={to ? "link" : undefined}
            title={to ? `Open ${label}` : undefined}
        >
            <div className="flex items-center justify-between text-muted-foreground">
                <span className="overline">{label}</span>
                <span className={accent || ""}>{icon}</span>
            </div>
            <div className={`mt-3 font-display font-black text-4xl tracking-tighter ${accent || ""}`}>
                {value}
            </div>
        </Card>
    );
}
